import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import anime from "animejs";
import * as PIXI from "pixi.js";
import store from "../State/Store/store";
import { rgsPlay } from "../State/Slices/spinSlice";
import { setReelsFrozen } from "../State/actions";
import { spinHeldOff } from "../utils/spinHeldOff";
import {
  selectShowTransitionDialog,
  selectIntroPopupActive,
  selectErrorPopupActive,
  selectInfoPopupActive,
  selectErrorRecoveryTransitionActive,
  selectShowHoldAndWinIntro,
  selectShowGameAfterTransition,
} from "../State/selectors";
import {
  selectHoldAndWinActive,
  selectHoldAndWinPhase,
  selectHoldAndWinReloadPhase,
  selectHoldAndWinRespinsLeft,
  clearHoldAndWinReload,
  setShowHoldAndWinReload,
  setShowHoldAndWinTotalWin,
  setShowHoldAndWinMaxWin,
  setShowHoldAndWinRoyal,
  selectHoldAndWinRoyalAmount,
  selectHoldAndWinTriggered,
} from "../State/Slices/holdAndWinSlice";
import { useReelRefs } from "./ReelRefsContext";
import {
  landingValues,
  playChestCollections,
} from "../Games/HoldAndWin/chestCollection";
import { playEndCollector } from "../Games/HoldAndWin/endCollector";
import {
  setHoldAndWinPace,
  holdAndWinSpinConfig,
  collectAlwaysSkipped,
} from "../Games/HoldAndWin/holdAndWinPace";
// import { logoPotWin, resetLogoPot } from "../Games/Shared/logoPot";
import { resetLogoPot } from "../Games/Shared/logoPot";
import { jackpotCoinSkin } from "../Games/HoldAndWin/jackpotCoin";
import {
  cellAnticipationApplies,
  cellAnticipationPlan,
} from "../Games/HoldAndWin/cellAnticipation";
import {
  // toScreenRow,
  openingScreenRow,
  isScreenRowOpen,
  // rowsForCellsInZone, (the early row openings are off, 2026-11-03)
  // ...back: the row opens as the NEXT spin starts (2026-11-03).
  rowsForCellsInZone,
} from "../RGS/Providers/PopiPlay/holdAndWinState";
import HoldAndWinCollectorController from "../Components/Animations/HoldAndWinCounter/HoldAndWinCollectorController";
import SpineAtlasUploader from "../utils/SpineAtlasUploader";
import { queueMeshWarm } from "../Games/Shared/spineMeshWarm";
import { useAppActivity } from "./AppActivityContext";
import { FEATURES_CONFIG } from "../featuresConfig";
import TickerTimeout from "../utils/TickerTimeout";
import { baseDiag } from "../utils/fsDiag";
import { soundManager } from "../SoundManager";
import { SOUND_NAME } from "../sounds";
import { createPlayButtonRotationAnimation } from "../utilities";

/**
 * HoldAndWinControlContext — Cleopatra's Fire Hold & Win respin round.
 *
 * NEW (Griffin's is gone). The round runs itself, like free spins: once the
 * room is on screen it sends `respin`, spins every empty cell, lands them on
 * the server's grid, and goes again — until the server settles the round.
 * The button never starts a spin; it lands the current one sooner, or resumes
 * a reloaded round (ButtonHoldAndWinPlay → handlePress, spacebar the same).
 *
 * ── Never one respin too many (the Zeus ban) ──
 *
 * A respin sent after the server closed the round answers 202, and a second
 * one locks the session for good. So a respin goes out only when ALL hold:
 *
 *   the room is active, phase 'respins', respins left > 0,
 *   the last step did not settle,
 *   the server OFFERS `respin` (flow.available_actions),
 *   nothing is in flight and nothing was refused (spinSlice's guard),
 *   and this context has not already claimed the respin (inFlightRef —
 *   taken synchronously, so the timer and a press can never both send).
 *
 * rgsPlay's `respin` path checks available_actions again, and its
 * `condition` refuses a second request in flight. A refusal freezes the
 * board and stops the loop; nothing is retried until a real resync.
 *
 * ── The respin counter (HoldAndWinCounter, the room's counter slot) ──
 *
 * Driven from here, on the respin's own beats (Pot Collection's timing):
 *   room uncovered   0 → 3              (a reload: the server's value, no clip)
 *   respin starts    −1                 (GDD §8.5 — never an ending on a 0)
 *   respin landed    the server's value (a reset animates back to 3)
 *
 * Not here yet: row unlocks, collectors, the ending.
 */

export const HoldAndWinControlContext = createContext(null);

// const spinConfig = () => FEATURES_CONFIG.HOLD_AND_WIN_SPIN || {};
// The respin's speed table (2026-10-23: turbo / super turbo in H&W) —
// HOLD_AND_WIN_SPIN with the pace's overrides (holdAndWinPace).
const spinConfig = () => holdAndWinSpinConfig();
// The player's speed now, as the pace for what starts next.
const paceFromPlayer = () => setHoldAndWinPace(store.getState().settings?.autoPlaySpeed);

/** Every condition for sending ONE respin, read from the store as it is now. */
const canSendRespin = (state) => {
  const hw = state.holdAndWin;
  const actions = state.game?.availableActions;
  return (
    hw.active &&
    hw.phase === "respins" &&
    hw.respinsLeft > 0 &&
    hw.step?.phase !== "settled" &&
    Array.isArray(actions) &&
    actions.includes("respin") &&
    !state.spin.rgsPlayInFlight &&
    !state.spin.playBlockedUntilResync &&
    // An init still open (the 105 fix, from 3 Orbs of Zeus): rgsPlay's
    // condition refuses a respin beside it, and a refused respin freezes the
    // room — so the loop waits instead (it re-runs when this clears).
    !spinHeldOff(state)
  );
};

/**
 * Run `apply(thing)` once `get()` returns something, checking each frame.
 * On a reload the slice is hydrated during init, BEFORE the room, its counter
 * and the play button exist — a one-shot effect found nothing and never ran
 * again (the counter sat at 0, no glow). Returns a cancel function.
 */
const whenReady = (get, apply, frames = 600) => {
  let raf = null;
  const tick = () => {
    const thing = get();
    if (thing) apply(thing);
    else if (frames-- > 0) raf = requestAnimationFrame(tick);
  };
  tick();
  return () => {
    if (raf) cancelAnimationFrame(raf);
  };
};

/** Filled cells on the grid — the coin counter's number (Royal = all 40). */
// ── The Royal popup, awaited ── Pot Collection's grid-full Grand Jackpot wait
// (PotCollectionSpinEngine): raise the flag, carry on when the popup's close
// drops it (a room teardown drops it too).
const awaitRoyalPopup = (dispatch) =>
  new Promise((resolve) => {
    dispatch(setShowHoldAndWinRoyal(true));
    const unsub = store.subscribe(() => {
      if (!store.getState().holdAndWin.showRoyal) {
        unsub();
        resolve();
      }
    });
  });
// Pot Collection's breath on the full board before its popup, ms.
const ROYAL_POPUP_DELAY_MS = 500;

const countFilledCells = (kinds) =>
  Array.isArray(kinds)
    ? kinds.reduce(
        (sum, column) =>
          sum + (Array.isArray(column) ? column.filter((kind) => kind != null).length : 0),
        0,
      )
    : 0;


export const HoldAndWinControlContextProvider = ({ children }) => {
  const dispatch = useDispatch();
  const {
    holdAndWinBoardRef,
    holdAndWinRespinCounterRef,
    holdAndWinGameViewControllerRef,
    playButtonRef,
  } = useReelRefs();
  const { isActive } = useAppActivity();

  const active = useSelector(selectHoldAndWinActive);
  const triggered = useSelector(selectHoldAndWinTriggered);
  const phase = useSelector(selectHoldAndWinPhase);
  const reloadPhase = useSelector(selectHoldAndWinReloadPhase);
  const respinsLeft = useSelector(selectHoldAndWinRespinsLeft);
  const transitionActive = useSelector(selectShowTransitionDialog);
  const introPopupActive = useSelector(selectIntroPopupActive);
  const introDialogActive = useSelector(selectShowHoldAndWinIntro);
  const errorPopupActive = useSelector(selectErrorPopupActive);
  // The free-rounds message a campaign reload opens on ("N FREE ROUNDS LEFT").
  const infoPopupActive = useSelector(selectInfoPopupActive);
  const errorRecoveryTransitionActive = useSelector(
    selectErrorRecoveryTransitionActive,
  );
  const showGameAfterTransition = useSelector(selectShowGameAfterTransition);
  // ── A bonus won inside free spins, reloaded (2026-10-08) ── the free
  // spins' "n LEFT" popup is shown first: this room's reload popup and its
  // settled collection wait until it is dismissed (freeSpinsSlice raises it
  // on the hydrate, the popup's close lowers it).
  const freeSpinsReloadOwed = useSelector(
    (state) => state.freeSpins.reloadPopupPending,
  );
  // An init still open — the loop waits (canSendRespin), and re-runs when it
  // clears (the 105 fix, from 3 Orbs of Zeus).
  const spinHeldOffNow = useSelector(spinHeldOff);

  // A reloaded round (mid-feature) waits for the player's press, as free
  // spins does. The slice raises it on the hydrate; the press clears it.
  const awaitingResume = active && reloadPhase === "in_progress";

  const [spinning, setSpinning] = useState(false);
  // The settlement (scarabs + End Collector) is playing: a press skips it, so
  // the play button must take presses then too (not only the spacebar).
  const [settling, setSettling] = useState(false);
  // The number on the button: −1 the moment a respin starts (GDD §8.5), the
  // server's value once it has landed.
  const [displayRespins, setDisplayRespins] = useState(respinsLeft);
  // Bumped when a respin has fully landed, so the loop decides again.
  const [landedTick, setLandedTick] = useState(0);

  const inFlightRef = useRef(false);
  const quickStopRef = useRef(false);
  // The active rows the player has SEEN open — a row opens on screen as the
  // count is reached, a respin before the server reports it (runRespin).
  const shownRowsRef = useRef(4);
  const failedRef = useRef(false);
  const firstRespinRef = useRef(true);
  const loopTimerRef = useRef(null);
  const timersRef = useRef([]);
  const pulseRef = useRef(null);
  // Cuts the minimum spin short — a press lands the cells NOW (Pot
  // Collection's fix: the stop fires the moment the data is there).
  const skipMinSpinRef = useRef(null);
  // The respin's cells are turning (start → landed) — only then may the speed
  // button's switch to turbo land them (requestTurboStop), never during the
  // collections after.
  const cellsTurningRef = useRef(false);
  // The counter has been put up for this visit to the room.
  const counterShownRef = useRef(false);

  const clearTimers = useCallback(() => {
    timersRef.current.forEach((t) => t?.clear?.());
    timersRef.current = [];
  }, []);

  // The counter follows the server whenever nothing is spinning.
  useEffect(() => {
    if (!spinning) setDisplayRespins(respinsLeft);
  }, [respinsLeft, spinning]);

  const stopPulse = useCallback(() => {
    if (pulseRef.current) {
      pulseRef.current.pause();
      pulseRef.current = null;
    }
    if (playButtonRef.current) playButtonRef.current.filters = [];
  }, [playButtonRef]);

  /**
   * ONE respin, start to landed. Claims inFlightRef before anything else, so
   * a second caller in the same tick finds it taken.
   */
  const runRespin = useCallback(async () => {
    if (inFlightRef.current || failedRef.current) return;
    if (!canSendRespin(store.getState())) return;
    const board = holdAndWinBoardRef.current;
    if (!board) return;

    inFlightRef.current = true;
    quickStopRef.current = false;
    firstRespinRef.current = false;
    // The counter finishes what it is showing (the entry fill, a reset)
    // before it counts this respin down.
    const counter = holdAndWinRespinCounterRef.current;
    await counter?.waitForIdle();
    // Everything checked again after the wait: nothing may have changed
    // under it (an error, the room closing) and still send.
    if (!canSendRespin(store.getState()) || !holdAndWinBoardRef.current) {
      inFlightRef.current = false;
      return;
    }
    const nextRespins = Math.max(0, store.getState().holdAndWin.respinsLeft - 1);
    counter?.playAnimation(nextRespins);
    setSpinning(true);
    setDisplayRespins(nextRespins);
    if (playButtonRef.current) createPlayButtonRotationAnimation(playButtonRef.current);

    // ── The row opens AS THIS SPIN STARTS (2026-11-03) ── the last respin
    // brought the zone to 10 / 15 / 20 / 25 (its plate reads 0): the server
    // opens that row on this respin, before its landings (GDD "Delayed Row
    // Unlock") — so no wait for the reply. The lock fades and what sleeps in
    // the row wakes while the cells spin; the landings wait for it below.
    let rowOpening = null;
    {
      const hw = store.getState().holdAndWin;
      const dueRows = rowsForCellsInZone(hw.cellsInZone ?? 0);
      const shownRows = Math.max(hw.unlockedRows, shownRowsRef.current);
      const overlay = holdAndWinBoardRef.current?.overlay?.();
      if (dueRows > shownRows && overlay) {
        shownRowsRef.current = dueRows;
        baseDiag("H&W row opens", { cellsInZone: hw.cellsInZone, rows: `${shownRows} -> ${dueRows}` });
        rowOpening = (async () => {
          await holdAndWinGameViewControllerRef.current?.unlockRows?.(shownRows, dueRows);
          const sleeping = [];
          for (let d = shownRows; d < dueRows; d++) {
            const screenRow = openingScreenRow(d);
            for (let c = 0; c < (hw.kinds?.length ?? 0); c++) {
              if (hw.kinds[c]?.[screenRow]) sleeping.push(overlay.playWin?.(c, screenRow));
            }
          }
          await Promise.all(sleeping);
        })();
      }
    }

    // const cfg = spinConfig();
    // const cells = board.spinEmpty(store.getState().holdAndWin.kinds);
    // ── This respin's speed (2026-10-23: turbo / super turbo in H&W) ── read
    // once, here: the cells, the landing and this respin's collections run at
    // it; a press of the speed button from now applies from the next respin.
    paceFromPlayer();
    const cfg = spinConfig();
    const superTurbo = collectAlwaysSkipped();
    // ── Super turbo DROPS ── the base game's super turbo: the cells stay
    // still until the reply (or SPIN_AFTER_WAIT_MS, so a slow server still
    // shows a working game), then all land at once. The empty cells are
    // taken NOW, before the reply fills some of them.
    const kindsAtStart = store.getState().holdAndWin.kinds;
    cellsTurningRef.current = true;
    let cells = null;
    const startCells = () => {
      if (!cells) cells = board.spinEmpty(kindsAtStart);
      return cells;
    };
    // if (!superTurbo) startCells();
    // SPIN_AFTER_WAIT_MS 0: on the press, as the base game's turbo (2026-10-23:
    // "no waiting for request").
    if (!superTurbo || (cfg.SPIN_AFTER_WAIT_MS ?? 0) <= 0) startCells();
    else {
      const dropWait = new TickerTimeout(() => {
        if (inFlightRef.current) startCells();
      }, cfg.SPIN_AFTER_WAIT_MS);
      timersRef.current.push(dropWait);
    }
    // ── The reel spin, as on the base reels (2026-10-19) ── the same loop,
    // from the cells starting to the last one landing. Stopped first: play()
    // on a looping Howl that is still going adds a second copy.
    // soundManager.stop(SOUND_NAME.reel_spin);
    // soundManager.play(SOUND_NAME.reel_spin);
    // Hold & Win's own spin loop (batch 3, 2026-10-22).
    soundManager.stop(SOUND_NAME.hold_and_win_reel_spin);
    // soundManager.play(SOUND_NAME.hold_and_win_reel_spin);
    // Not on a super-turbo drop (the base game skips its spin loop there too).
    // if (!superTurbo) soundManager.play(SOUND_NAME.hold_and_win_reel_spin);
    // Whenever the cells are turning — super turbo turns them on the press now.
    if (cells) soundManager.play(SOUND_NAME.hold_and_win_reel_spin);
    // ── The last cells' anticipation starts WITH the spin (2026-10-07) ──
    // 1-2 cells left is known now, before the answer (Pot Collection: "works
    // instantly during spin"): the first cell's anticipation and the room
    // frame start here; the answer below only says where the chain ends.
    // const anticipationHooks = {
    //   loops: cfg.CELL_ANTICIPATION?.LOOPS ?? 1,
    //   onStart: () => holdAndWinGameViewControllerRef.current?.playGridAnticipation?.(),
    //   onEnd: () => holdAndWinGameViewControllerRef.current?.stopGridAnticipation?.(),
    //   onCancel: () => holdAndWinGameViewControllerRef.current?.hideGridAnticipation?.(),
    // };
    // ── The Royal plate glows WITH the chain (Vladimir, 2026-10-07) ──
    // rjp_anticipation starts with the cells and the frame; at the chain's
    // end it goes — unless this respin awards the Royal (set once the answer
    // is here, below): then the 40th coin's landing switches it to the win
    // glow (onLanded), which stays until the Royal popup has closed.
    let royalThisRespin = false;
    const room = () => holdAndWinGameViewControllerRef.current;
    const anticipationHooks = {
      loops: cfg.CELL_ANTICIPATION?.LOOPS ?? 1,
      onStart: () => {
        room()?.playGridAnticipation?.();
        room()?.playRoyalAnticipation?.();
        // Its fire and drums — from the first anticipation on, through every
        // respin that anticipates (ended after the landing, below).
        room()?.startAnticipationSounds?.();
      },
      onEnd: () => {
        room()?.stopGridAnticipation?.();
        if (!royalThisRespin) room()?.stopRoyalAnticipation?.();
      },
      onCancel: () => {
        room()?.hideGridAnticipation?.();
        room()?.hideRoyalPlate?.();
        room()?.finishAnticipationSounds?.();
      },
    };
    // const anticipating =
    //   cellAnticipationApplies(cells) &&
    //   !!board.beginAnticipation?.(cells, anticipationHooks);
    // None in super turbo: every cell drops at once.
    const anticipating =
      !superTurbo &&
      cellAnticipationApplies(cells) &&
      !!board.beginAnticipation?.(cells, anticipationHooks);
    // The minimum spin — which a press cuts short (skipMinSpinRef), so the
    // cells land the moment the response is here, not up to 600 ms later.
    const minSpin = new Promise((resolve) => {
      const timer = new TickerTimeout(resolve, cfg.RESPIN_MIN_SPIN_MS ?? 600);
      timersRef.current.push(timer);
      skipMinSpinRef.current = () => {
        timer.clear();
        resolve();
      };
    });
    if (quickStopRef.current) skipMinSpinRef.current();

    // The grid BEFORE this response — a golden chest recollecting starts its
    // count from its old value.
    const prevValues = store.getState().holdAndWin.values;
    // The active rows BEFORE this response — a row the server opens this step
    // is shown opening before anything lands (GDD "Row opening").
    const prevUnlockedRows = store.getState().holdAndWin.unlockedRows;
    // The cells standing BEFORE this response — the coin counter counts up from
    // here, one per landing (Pot Collection's incrementPotCollectionCoinCount).
    const prevFilled = countFilledCells(store.getState().holdAndWin.kinds);
    // Royal already awarded BEFORE this response — the popup plays on the
    // step that records it, once.
    const prevRoyal = !!store.getState().holdAndWin.royalJackpot;
    const action = await dispatch(rgsPlay({ respin: true }));
    const hw = action?.payload?.holdAndWin;

    // ── Refused (by the server, by our guard, or not sent at all) ──
    // Freeze where they are and stop the loop. spinSlice's
    // playBlockedUntilResync keeps every further send shut until a resync.
    if (!rgsPlay.fulfilled.match(action) || !hw) {
      failedRef.current = true;
      cellsTurningRef.current = false;
      // soundManager.stop(SOUND_NAME.reel_spin);
      soundManager.stop(SOUND_NAME.hold_and_win_reel_spin);
      // ...and an anticipation's fire and drums with it.
      room()?.finishAnticipationSounds?.();
      holdAndWinBoardRef.current?.freeze();
      dispatch(setReelsFrozen(true));
      inFlightRef.current = false;
      setSpinning(false);
      return;
    }

    await minSpin;
    skipMinSpinRef.current = null;
    // The drop: the cells start now if the reply beat the wait (and land
    // together below).
    startCells();

    // ── A row opens BEFORE the landings (GDD "Row opening (anim.)":
    // "Activate row before presenting new landings") ──
    // The threshold was reached on an earlier respin; the server opens the row
    // on this one. The reels hold, the row's lock fades, the symbols already
    // standing in it wake with their `win` — then this respin lands. Opening a
    // row gives no respins and resets nothing (GDD): the counter is untouched.
    // // ── Only a FALLBACK now ── the row normally opened already, as the
    // // landing that reached the count played (see the end of this respin).
    // // This catches a row the server opened that the count did not predict.
    // ── THE opening again (QA, 2026-11-03) ── the early ones are off.
    // ...a backstop once more: the row opened as this spin started (above).
    // The landings wait for that opening to finish.
    await rowOpening;
    const nextUnlockedRows = store.getState().holdAndWin.unlockedRows;
    const shownRows = Math.max(prevUnlockedRows, shownRowsRef.current);
    if (nextUnlockedRows > shownRows) {
      const overlay = holdAndWinBoardRef.current?.overlay?.();
      shownRowsRef.current = nextUnlockedRows;
      if (overlay) {
        // await overlay.unlockRows?.(shownRows, nextUnlockedRows);
        // The room's lock plates (locked_reel), not the overlay's bars.
        await holdAndWinGameViewControllerRef.current?.unlockRows?.(shownRows, nextUnlockedRows);
        const sleeping = [];
        const prevKinds = store.getState().holdAndWin.kinds;
        for (let d = shownRows; d < nextUnlockedRows; d++) {
          // const screenRow = toScreenRow(d);
          const screenRow = openingScreenRow(d);
          for (let c = 0; c < (prevKinds?.length ?? 0); c++) {
            // Only what stood there BEFORE this response — this step's own
            // landings have not landed yet.
            const isNew = (store.getState().holdAndWin.step?.newCells || []).some(
              (cell) => cell.reel === c && cell.screenRow === screenRow,
            );
            if (prevKinds[c]?.[screenRow] && !isNew) {
              sleeping.push(overlay.playWin?.(c, screenRow));
            }
          }
        }
        await Promise.all(sleeping);
      }
    }

    // The grid as the server now has it (screen rows), from the slice. A
    // chest collecting this step lands EMPTY — its value comes with the
    // collection below.
    const { kinds, values, points, step, unlockedRows } = store.getState().holdAndWin;
    // This respin awards the Royal (the grid fills with every row open).
    royalThisRespin = !prevRoyal && !!store.getState().holdAndWin.royalJackpot;
    const totalCells = (kinds?.length ?? 0) * (kinds?.[0]?.length ?? 0);
    // What this respin landed and where — screen [column,row] (row 0 = top),
    // and whether the server counts it in the active zone.
    baseDiag("H&W respin", {
      respinsLeft: store.getState().holdAndWin.respinsLeft,
      unlockedRows,
      shownRows: shownRowsRef.current,
      landed: JSON.stringify(
        (step?.newCells || []).map(
          // (c) => `${c.kind}@${c.reel},${c.screenRow}${c.row < unlockedRows ? "" : "(locked)"}`,
          (c) =>
            `${c.kind}@${c.reel},${c.screenRow}${isScreenRowOpen(c.screenRow, unlockedRows) ? "" : "(locked)"}`,
        ),
      ),
    });

    // ── The counter resets AS a qualifying symbol LANDS (Pot Collection) ──
    // Not after the whole respin: the first new cell landing INSIDE the
    // active zone puts the counter back to the server's value (3). Locked-row
    // landings never reset it (GDD). With none in the zone, the −1 stands and
    // is confirmed once the landing is over.
    const zoneLandings = new Set(
      (step?.newCells || [])
        // .filter((cell) => cell.row < unlockedRows)
        .filter((cell) => isScreenRowOpen(cell.screenRow, unlockedRows))
        .map((cell) => `${cell.reel}_${cell.screenRow}`),
    );
    let counterSet = false;
    const setCounter = () => {
      if (counterSet) return;
      counterSet = true;
      // holdAndWinRespinCounterRef.current?.playAnimation(
      //   store.getState().holdAndWin.respinsLeft,
      // );
      // ── The button's number WITH the counter (2026-10-14: "they have to
      // update at same time … as soon as coin land") ── it waited for the
      // respin's end; now the counter spine, the button and the coin counter
      // (onLanded) all change on the same landing.
      const left = store.getState().holdAndWin.respinsLeft;
      holdAndWinRespinCounterRef.current?.playAnimation(left);
      setDisplayRespins(left);
    };
    // ── Every landing updates the coin counter AT ONCE ── Pot Collection's
    // rule (ScreenProgressProcessor: one incrementPotCollectionCoinCount per
    // coin as it lands). Vladimir, 2026-09-29: "as soon as COIN LANDS DOESNT
    // MATTER WHERE, WE UPDATE IT". And a landing inside the zone that brings it
    // to 10 / 15 / 20 / 25 opens the next row right there (openDueRows).
    let shownFilled = prevFilled;
    const afterResponse = store.getState().holdAndWin;
    // The zone count before this step's zone landings, so each one adds one
    // and the last lands exactly on the server's cells_in_zone.
    let zoneCount = (afterResponse.cellsInZone ?? 0) - zoneLandings.size;
    // (Off 2026-11-03 — the row opens at the next respin's start, see onLanded.)
    // const openDueRows = (count) => {
    //   const dueRows = rowsForCellsInZone(count);
    //   const alreadyShown = Math.max(afterResponse.unlockedRows, shownRowsRef.current);
    //   // const rowOverlay = holdAndWinBoardRef.current?.overlay?.();
    //   // The room's lock plates (locked_reel), not the overlay's bars.
    //   const rowOverlay = holdAndWinGameViewControllerRef.current;
    //   // Not on a settling step — there is no next respin to open it for.
    //   if (afterResponse.respinsLeft > 0 && dueRows > alreadyShown && rowOverlay?.unlockRows) {
    //     shownRowsRef.current = dueRows;
    //     baseDiag("H&W row opens", { cellsInZone: count, rows: `${alreadyShown} -> ${dueRows}` });
    //     rowOverlay.unlockRows(alreadyShown, dueRows); // non-blocking
    //   }
    // };
    // const onLanded = (col, row) => {
    //   if (zoneLandings.has(`${col}_${row}`)) setCounter();
    // };
    const onLanded = (col, row) => {
      // Every spinning cell reports its stop — most land EMPTY. Only a cell
      // that landed a symbol counts (it was empty before this respin, so
      // anything standing there now is this step's landing).
      if (!kinds?.[col]?.[row]) return;
      // A jackpot coin: its plate reacts as it lands (GDD: jackpot coins
      // "triggering the fixed jackpots"). No-op until the H&W room's spine
      // has the jackpot_<tier>_hit clips.
      const jackpotSkin = jackpotCoinSkin(
        points?.[col]?.[row],
        store.getState().game.jackpotMultipliers,
      );
      if (jackpotSkin) {
        holdAndWinGameViewControllerRef.current?.playJackpotHit?.(
          jackpotSkin.replace(/^Jp_/, ""),
        );
      }
      shownFilled += 1;
      holdAndWinGameViewControllerRef.current?.setCoinCounter(shownFilled);
      // ── The 40th coin: the Royal plate's win glow (2026-10-07) ── through
      // the Royal popup, out when the collection starts (below).
      if (royalThisRespin && totalCells > 0 && shownFilled >= totalCells) {
        room()?.playRoyalWin?.();
      }
      if (zoneLandings.has(`${col}_${row}`)) {
        setCounter();
        zoneCount += 1;
        // The lock plates' "N MORE", landing by landing.
        holdAndWinGameViewControllerRef.current?.setZoneCount?.(zoneCount);
        // openDueRows(zoneCount);
        // ── NOT on the landing (QA, 2026-11-03, GDD "Delayed Row Unlock") ──
        // the server opens the row on the FOLLOWING respin, before its
        // landings. Opened here, a chest landing later in the same respin sat
        // in a row that looked open but was still locked (pending, no collect).
        // The row now opens only at the next respin's start (nextUnlockedRows).
      }
    };

    // ── The last cells' anticipation (Vladimir, 2026-10-07) ── decided ONCE,
    // from this response (cellAnticipationPlan): begun with the spin (1-2
    // cells spinning — this only cuts it), or due MID-respin: the last 1-2
    // cells go into it as the cell before them lands, when everything before
    // them lands something (the 38th coin). Not after a press — the player
    // asked for the landing now.
    const plan = cellAnticipationPlan(cells, kinds);
    let anticipationChain = null;
    if (anticipating && plan) {
      anticipationChain = { startAt: 0, cells: plan.cells };
    // } else if (!anticipating && plan?.startAt > 0 && !quickStopRef.current) {
    } else if (!anticipating && plan?.startAt > 0 && !quickStopRef.current && !superTurbo) {
      anticipationChain = { startAt: plan.startAt, cells: plan.cells, hooks: anticipationHooks };
    }
    const anticipationCells = anticipationChain?.cells ?? null;
    if (anticipationChain) {
      baseDiag("H&W cell anticipation", {
        spinning: cells.length,
        chain: anticipationCells.map(([c, r]) => `${c},${r}`).join(" "),
      });
    }
    const landing = holdAndWinBoardRef.current?.land(
      cells,
      kinds,
      landingValues(values, step),
      quickStopRef.current || superTurbo ? 0 : cfg.RESPIN_STOP_GAP_MS ?? 40,
      points,
      store.getState().game.jackpotMultipliers, // jackpot coins (cj)
      onLanded,
      anticipationChain,
    );
    // if (quickStopRef.current) holdAndWinBoardRef.current?.quickStop();
    // Super turbo lands as a quick stop: all together, one stop sound.
    if (quickStopRef.current || superTurbo) holdAndWinBoardRef.current?.quickStop();
    await landing;
    cellsTurningRef.current = false;
    // soundManager.stop(SOUND_NAME.reel_spin);
    soundManager.stop(SOUND_NAME.hold_and_win_reel_spin);
    // ── The anticipation's fire and drums (2026-10-22) ── they go on into the
    // next respin when it anticipates from its start (respins left, not
    // settled, only 1-2 empty cells to spin); otherwise this landing was the
    // last coin and they finish now.
    {
      const hw = store.getState().holdAndWin;
      const empties = [];
      (kinds || []).forEach((column, c) =>
        (column || []).forEach((kind, r) => {
          if (!kind) empties.push([c, r]);
        }),
      );
      const nextAnticipates =
        hw.respinsLeft > 0 && hw.step?.phase !== "settled" && cellAnticipationApplies(empties);
      if (!nextAnticipates) room()?.finishAnticipationSounds?.();
    }
    // Reconcile: the counter has been counting landing by landing (onLanded);
    // this only corrects it if a landing went uncounted (a quick stop).
    holdAndWinGameViewControllerRef.current?.setCoinCounter(
      countFilledCells(store.getState().holdAndWin.kinds),
    );
    // The same for the lock plates' "N MORE": the server's count.
    holdAndWinGameViewControllerRef.current?.setZoneCount?.(
      store.getState().holdAndWin.cellsInZone ?? 0,
    );
    // ── The row opens WITH the coin counter — and blocks nothing ──
    // (Vladimir, 2026-09-29: "as soon as we update the coin counter this
    // plays in a non blocking way".) The landing that brings the active zone
    // to 10 / 15 / 20 / 25 symbols (the server's cells_in_zone, GDD
    // thresholds) opens the next row — the server applies it on the next
    // respin. A short fade, not awaited: the round goes straight on. Not after
    // a settling step — there is no next respin to open it for.
    // ── ...not at the end of the respin either (QA, 2026-11-03, GDD "Delayed
    // Row Unlock") ── the next respin opens it, before its landings.
    // {
    //   const afterStep = store.getState().holdAndWin;
    //   const dueRows = rowsForCellsInZone(afterStep.cellsInZone);
    //   const alreadyShown = Math.max(afterStep.unlockedRows, shownRowsRef.current);
    //   // const rowOverlay = holdAndWinBoardRef.current?.overlay?.();
    //   const rowOverlay = holdAndWinGameViewControllerRef.current;
    //   if (afterStep.respinsLeft > 0 && dueRows > alreadyShown && rowOverlay?.unlockRows) {
    //     shownRowsRef.current = dueRows;
    //     baseDiag("H&W row opens", {
    //       cellsInZone: afterStep.cellsInZone,
    //       rows: `${alreadyShown} -> ${dueRows}`,
    //     });
    //     rowOverlay.unlockRows(alreadyShown, dueRows);
    //   }
    // }

    // The row opening used to START here, beside the landing — too early and
    // too short to see; it now runs as its own step after everything else
    // (see "The row opens" below). Kept for reference:
    // // ── The row opens AS THE COUNT IS REACHED ──
    // // The landing that brings the active zone to 10 / 15 / 20 / 25 symbols
    // // opens the next row (the server applies it on the next respin). So the
    // // lock fades now, while the new symbols play their in -> win -> idle, and
    // // the NEXT respin waits for the fade to finish (awaited at the end of this
    // // one). One after another, no timer (Vladimir, 2026-09-29). Not after a
    // // settling step — there is no next respin to open it for.
    // let rowOpening = null;
    // const afterLanding = store.getState().holdAndWin;
    // const dueRows = rowsForCellsInZone(afterLanding.cellsInZone);
    // const alreadyShown = Math.max(afterLanding.unlockedRows, shownRowsRef.current);
    // if (afterLanding.respinsLeft > 0 && dueRows > alreadyShown) {
    //   const overlay = holdAndWinBoardRef.current?.overlay?.();
    //   shownRowsRef.current = dueRows;
    //   if (overlay?.unlockRows) {
    //     rowOpening = overlay.unlockRows(alreadyShown, dueRows);
    //     // What already sleeps in the opening row wakes with its `win` — not
    //     // this respin's own landings there, which are still playing theirs.
    //     const landedNow = new Set(
    //       (afterLanding.step?.newCells || []).map((cell) => `${cell.reel}_${cell.screenRow}`),
    //     );
    //     for (let d = alreadyShown; d < dueRows; d++) {
    //       const screenRow = toScreenRow(d);
    //       for (let c = 0; c < (afterLanding.kinds?.length ?? 0); c++) {
    //         if (afterLanding.kinds[c]?.[screenRow] && !landedNow.has(`${c}_${screenRow}`)) {
    //           overlay.playWin?.(c, screenRow);
    //         }
    //       }
    //     }
    //   }
    // }

    // The server's count: reset on the first zone landing above, or the −1
    // standing (nothing landed in the zone) — confirmed here.
    setCounter();
    // ── A stop press stops the REELS, nothing more ──
    // The flag is spent now the cells have landed. Left raised, a manual stop
    // also skipped the chests' collection (all trails at once) — Pot
    // Collection collects one by one, and only a NEW press during the
    // collection speeds it up (Vladimir, 2026-09-29). handlePress raises it
    // again for that: inFlightRef is still true until the respin ends.
    quickStopRef.current = false;
    // The chests collect (GDD "Resolve Collectors") — after the landings and
    // the counter, one at a time. A press skips it as Pot Collection's slam
    // stop does. The coins keep their values.
    await playChestCollections({
      step,
      values,
      prevValues,
      unlockedRows,
      overlay: holdAndWinBoardRef.current?.overlay?.(),
      trailLayer: holdAndWinGameViewControllerRef.current?.trailLayer,
      // isSkipped: () => quickStopRef.current,
      // Super turbo: always as a skip (holdAndWinPace).
      isSkipped: () => quickStopRef.current || collectAlwaysSkipped(),
    });
    // (The coin counter and the row opening moved up to the landing.)
    // The next respin waits for the row to finish opening.
    // if (rowOpening) await rowOpening;

    // ── The Royal, ON THE STEP THAT RECORDS IT ── (raw GDD "Royal Award",
    // workflow 3: "Present the Royal award on the step that records it".)
    // Pot Collection's grid-full Grand Jackpot: the last landing and the
    // chests done, the Royal plate lights, a breath on the full board, the
    // popup — and the round carries on when it closes (the End Collector then
    // flies the Royal in, GDD step 6). Not on a reload: already recorded.
    if (!prevRoyal && store.getState().holdAndWin.royalJackpot) {
      holdAndWinGameViewControllerRef.current?.playJackpotHit?.("royal");
      await new Promise((resolve) => {
        const timer = new TickerTimeout(resolve, ROYAL_POPUP_DELAY_MS);
        timersRef.current.push(timer);
      });
      if (store.getState().holdAndWin.active) await awaitRoyalPopup(dispatch);
      // The popup has closed — the collection starts: the plate's glow goes.
      room()?.stopRoyalWin?.();
    }

    // The blocking version (the round waited for the landings, the fade and
    // the wake) felt like an odd delay — replaced by the non-blocking opening
    // at the coin counter above. Kept for reference:
    // // ── The row opens — its own step, after everything else ──
    // // (Vladimir, 2026-09-29: "we finish spin, we land on 10th coin, we open
    // // this, once its open we proceed with respins".) The landing that brings
    // // the active zone to 10 / 15 / 20 / 25 symbols (the server's
    // // cells_in_zone, GDD thresholds) opens the next row; the server applies it
    // // on the next respin. One after another, each on its own completion:
    // //   1. this respin's landings play out (their idle has started)
    // //   2. the row's lock fades away
    // //   3. what already slept in the row wakes with its `win`
    // // and only then does the loop send the next respin. Not after a settling
    // // step — there is no next respin to open it for.
    // const afterStep = store.getState().holdAndWin;
    // const dueRows = rowsForCellsInZone(afterStep.cellsInZone);
    // const alreadyShown = Math.max(afterStep.unlockedRows, shownRowsRef.current);
    // const rowOverlay = holdAndWinBoardRef.current?.overlay?.();
    // if (afterStep.respinsLeft > 0 && dueRows > alreadyShown && rowOverlay?.unlockRows) {
    //   shownRowsRef.current = dueRows;
    //   const newCells = afterStep.step?.newCells || [];
    //   await Promise.all(
    //     newCells.map(({ reel, screenRow }) => rowOverlay.landed?.(reel, screenRow)),
    //   );
    //   baseDiag("H&W row opens", {
    //     cellsInZone: afterStep.cellsInZone,
    //     rows: `${alreadyShown} -> ${dueRows}`,
    //   });
    //   await rowOverlay.unlockRows(alreadyShown, dueRows);
    //   const landedNow = new Set(newCells.map((cell) => `${cell.reel}_${cell.screenRow}`));
    //   const waking = [];
    //   for (let d = alreadyShown; d < dueRows; d++) {
    //     const screenRow = toScreenRow(d);
    //     for (let c = 0; c < (afterStep.kinds?.length ?? 0); c++) {
    //       if (afterStep.kinds[c]?.[screenRow] && !landedNow.has(`${c}_${screenRow}`)) {
    //         waking.push(rowOverlay.playWin?.(c, screenRow));
    //       }
    //     }
    //   }
    //   await Promise.all(waking);
    //   baseDiag("H&W row open", { rows: dueRows });
    // }

    inFlightRef.current = false;
    setSpinning(false);
    setLandedTick((n) => n + 1);
  }, [
    dispatch,
    holdAndWinBoardRef,
    holdAndWinRespinCounterRef,
    holdAndWinGameViewControllerRef,
    playButtonRef,
  ]);

  // // ── The logo Pot (GDD "POT upgrade stages") ──
  // // H&W triggering is the Pot's WIN, from whatever stage it stood at (a
  // // Random Pot included); the round over, the cup starts empty again.
  // // ── Only once the trigger has LANDED ── The response arrives while the reels
  // // still turn; the GDD's blast follows the coins landing, so the WIN waits
  // // for the triggering spin to have stopped (the base game's spinComplete, or
  // // the free spin's when it came from there).
  // const baseSpinComplete = useSelector((state) => state.spin.spinComplete);
  // const freeSpinComplete = useSelector((state) => state.freeSpins.spinComplete);
  // const fromFreeSpinsRoom = useSelector((state) => state.freeSpins.active);
  // const wasTriggeredRef = useRef(false);
  // useEffect(() => {
  //   // const landed = fromFreeSpinsRoom ? freeSpinComplete : baseSpinComplete;
  //   if (triggered) {
  //     // A Random Pot plays its blast in its own sequence, after the paid
  //     // spin's wins (ReelControlContext) — not on the paid spin's landing.
  //     // if ((landed || active) && !store.getState().holdAndWin.randomPot) logoPotWin();
  //     // ── A natural trigger leaves the Pot alone (PO, 2026-10-28: "the only
  //     // time logo state should reset is when we actually get that round
  //     // triggered by logo") ── no rise to 4, no blast; the fire keeps its
  //     // stage through the bonus. Only the Random Pot fires it (above), and
  //     // only that round empties it (resetLogoPot resets a WON cup only).
  //   } else if (wasTriggeredRef.current) {
  //     resetLogoPot();
  //   }
  //   wasTriggeredRef.current = triggered;
  // }, [triggered, active, baseSpinComplete, freeSpinComplete, fromFreeSpinsRoom]);
  // ── The logo Pot (GDD "POT upgrade stages") — the round's END only ──
  // (PO, 2026-10-28: "the only time logo state should reset is when we
  // actually get that round triggered by logo".) A natural Hold & Win leaves
  // the cup alone; only the Random Pot fires it (logoPotWin, in
  // ReelControlContext's Random Pot sequence) and only that round empties it
  // when it ends. "That round" is either the cup's own `won` mark or the
  // round's randomPot flag seen while it ran — the second covers a reload past
  // the blast, where this browser never marked the cup.
  const wasTriggeredRef = useRef(false);
  const wasRandomPotRef = useRef(false);
  useEffect(() => {
    if (triggered) {
      if (store.getState().holdAndWin.randomPot) wasRandomPotRef.current = true;
    } else if (wasTriggeredRef.current) {
      resetLogoPot({ randomPotRound: wasRandomPotRef.current });
      wasRandomPotRef.current = false;
    }
    wasTriggeredRef.current = triggered;
  }, [triggered, active]);

  // ── The locked rows: the grid the room opens on ──
  // Their plates go on the moment the room is on screen (the wipe's midpoint,
  // or a reload), matching the server's active rows.
  // useEffect(() => {
  //   if (!active) return undefined;
  //   return whenReady(
  //     () => holdAndWinBoardRef.current?.overlay?.()?.setUnlockedRows && holdAndWinBoardRef.current.overlay(),
  //     (overlay) => {
  //       const rows = store.getState().holdAndWin.unlockedRows;
  //       shownRowsRef.current = rows;
  //       overlay.setUnlockedRows(rows);
  //     },
  //   );
  // }, [active, holdAndWinBoardRef]);
  // The room's lock plates (locked_reel, HoldAndWinGameViewController).
  useEffect(() => {
    if (!active) return undefined;
    return whenReady(
      () => holdAndWinGameViewControllerRef.current?.spine && holdAndWinGameViewControllerRef.current,
      (controller) => {
        const rows = store.getState().holdAndWin.unlockedRows;
        shownRowsRef.current = rows;
        controller.setUnlockedRows(rows);
        // Every round starts with the box at its respin size.
        controller.resetReelZoom?.();
        controller.setZoneCount(store.getState().holdAndWin.cellsInZone ?? 0);
      },
    );
  }, [active, holdAndWinGameViewControllerRef]);

  // ── The coin counter (Royal plate): the grid the room opens on ──
  // Set the moment the room is on screen (the wipe's midpoint, or a reload),
  // so it matches the coins already standing there.
  useEffect(() => {
    if (!active) return undefined;
    return whenReady(
      () => holdAndWinGameViewControllerRef.current?.spine && holdAndWinGameViewControllerRef.current,
      (controller) =>
        controller.setCoinCounter(countFilledCells(store.getState().holdAndWin.kinds)),
    );
  }, [active, holdAndWinGameViewControllerRef]);

  // ── The counter, when the room is uncovered ──
  // A live entry fills it (0 → 3); a reload shows where the round stands,
  // with no decrease or reset replayed (GDD §8.5).
  // useEffect(() => {
  //   if (!active || transitionActive || counterShownRef.current) return undefined;
  //   return whenReady(
  //     () => {
  //       const counter = holdAndWinRespinCounterRef.current;
  //       return counter?.isReady?.() ? counter : null;
  //     },
  //     (counter) => {
  //       counterShownRef.current = true;
  //       if (awaitingResume || phase !== "respins") {
  //         counter.setValue(store.getState().holdAndWin.respinsLeft);
  //       } else {
  //         counter.showFull();
  //       }
  //     },
  //   );
  // }, [active, transitionActive, awaitingResume, phase, holdAndWinRespinCounterRef]);
  // ── No entry fill (Vladimir, 2026-10-01: "can it just be on 3 right
  // away?") ── the room opens with the counter already on the server's value
  // (3 on a live entry), set at rest the moment the room is up — under the
  // wipe, so it is never seen empty. A reload does the same, as before.
  useEffect(() => {
    if (!active || counterShownRef.current) return undefined;
    return whenReady(
      () => {
        const counter = holdAndWinRespinCounterRef.current;
        return counter?.isReady?.() ? counter : null;
      },
      (counter) => {
        counterShownRef.current = true;
        counter.setValue(store.getState().holdAndWin.respinsLeft);
      },
    );
  }, [active, holdAndWinRespinCounterRef]);

  // ── The loop ──
  //
  // Returns WITHOUT scheduling whenever something owns the screen; it runs
  // again when that clears. The timer is cleared on every dependency change,
  // so nothing armed under one state fires under another.
  useEffect(() => {
    if (!active || spinning || inFlightRef.current || failedRef.current) return undefined;
    if (phase !== "respins") return undefined;
    if (transitionActive || introPopupActive || introDialogActive) return undefined;
    if (errorPopupActive || errorRecoveryTransitionActive || !isActive) return undefined;
    if (awaitingResume) return undefined;
    if (!canSendRespin(store.getState())) return undefined;

    // The wait before the next respin at the player's speed now (turbo's is
    // shorter — holdAndWinPace, 2026-10-23).
    paceFromPlayer();
    const cfg = spinConfig();
    const delay = firstRespinRef.current
      ? cfg.RESPIN_FIRST_DELAY_MS ?? 800
      : cfg.RESPIN_NEXT_DELAY_MS ?? 700;
    loopTimerRef.current = new TickerTimeout(() => {
      loopTimerRef.current = null;
      runRespin();
    }, delay);
    return () => {
      loopTimerRef.current?.clear();
      loopTimerRef.current = null;
    };
  }, [
    active,
    spinning,
    phase,
    landedTick,
    transitionActive,
    introPopupActive,
    introDialogActive,
    errorPopupActive,
    errorRecoveryTransitionActive,
    isActive,
    awaitingResume,
    spinHeldOffNow,
    runRespin,
  ]);

  // ── The reload popup (YOU HAVE / n / RESPIN(S) LEFT) ──
  // A reloaded round with respins left, once the game is on screen and
  // nothing else owns it. Once per parking. Its close starts NOTHING (Mad
  // Labs): the round stays parked on the glowing button and the player's
  // press resumes it. See HoldAndWinIntroDialog "reload".
  const reloadPopupRaisedRef = useRef(false);
  useEffect(() => {
    if (!awaitingResume) {
      reloadPopupRaisedRef.current = false;
      return;
    }
    if (reloadPopupRaisedRef.current || phase !== "respins") return;
    if (!showGameAfterTransition || introPopupActive) return;
    if (freeSpinsReloadOwed) return;
    if (transitionActive || errorPopupActive) return;
    reloadPopupRaisedRef.current = true;
    dispatch(setShowHoldAndWinReload(true));
  }, [
    awaitingResume,
    phase,
    showGameAfterTransition,
    introPopupActive,
    freeSpinsReloadOwed,
    transitionActive,
    errorPopupActive,
    dispatch,
  ]);

  // ── A resync after a refusal re-arms the round ──
  // The recovery (refreshGameData) hydrates the slice as a reload: the round
  // parks on the button again, and only a press sends.
  useEffect(() => {
    if (reloadPhase === "in_progress") failedRef.current = false;
  }, [reloadPhase]);

  // ── The waiting glow (a reloaded round) ── Pot Collection's pulse.
  useEffect(() => {
    if (!awaitingResume || errorPopupActive || transitionActive) return undefined;
    // Only a round with respins left waits for a press. A settled one (0 left)
    // collects by itself — nothing to press, so no glow (2026-10-19).
    if (phase !== "respins") return undefined;
    // The button is swapped in after the reload restores the room — wait for
    // its sprite (the shared playButtonRef) rather than finding nothing.
    const cancelWait = whenReady(
      () => {
        const sprite = playButtonRef.current;
        return sprite && !sprite.destroyed ? sprite : null;
      },
      (sprite) => startPulse(sprite),
    );
    return () => {
      cancelWait();
      stopPulse();
    };
  }, [awaitingResume, errorPopupActive, transitionActive, phase, playButtonRef, stopPulse]);

  /** Pot Collection's pulse, on the play button sprite. */
  const startPulse = (sprite) => {
    if (pulseRef.current) return;
    const pulseFilter = new PIXI.ColorMatrixFilter();
    pulseFilter.padding = 8;
    sprite.filters = [pulseFilter];
    const proxy = { brightness: 1.0 };
    pulseRef.current = anime({
      targets: proxy,
      brightness: 2.5,
      duration: 300,
      easing: "easeInOutSine",
      direction: "alternate",
      loop: true,
      update: () => {
        if (!sprite || sprite.destroyed) {
          pulseRef.current?.pause();
          pulseRef.current = null;
          return;
        }
        if (!sprite.filters || !sprite.filters.includes(pulseFilter)) {
          sprite.filters = [pulseFilter];
        }
        pulseFilter.brightness(proxy.brightness, false);
      },
    });
  };

  /**
   * One press (button or spacebar):
   *   1  a reloaded round is parked  → resume it (one press, one respin)
   *   2  cells are turning           → land them all now (GDD §12)
   * Anything else does nothing — a press never starts or queues a respin.
   */
  const handlePress = useCallback(() => {
    const state = store.getState();
    if (!state.holdAndWin.active) return;

    if (state.holdAndWin.reloadPhase === "in_progress") {
      if (state.userInterface?.errorPopupActive) return;
      // The reload popup owns the press; its close resumes the round.
      if (state.holdAndWin.showReloadPopup) return;
      soundManager.play(SOUND_NAME.button_click);
      stopPulse();
      dispatch(clearHoldAndWinReload());
      runRespin();
      return;
    }

    // ── Super turbo: nothing to skip (2026-10-23) ── its respins drop at
    // once and its collections already run as a skip, so a press does
    // nothing more (the resume above still works).
    if (collectAlwaysSkipped()) return;

    // ── The last cells' anticipation owns the press ── the cell on its turn
    // stops now, its out plays in full (the base chain's skipActiveReel); the
    // next press takes the next cell. A press during an out does nothing.
    if (inFlightRef.current && holdAndWinBoardRef.current?.skipAnticipationCell?.()) {
      soundManager.play(SOUND_NAME.button_click);
      // Before the answer: no minimum spin left to wait out either.
      skipMinSpinRef.current?.();
      return;
    }

    if (inFlightRef.current && !quickStopRef.current) {
      soundManager.play(SOUND_NAME.button_click);
      quickStopRef.current = true;
      // Before the landing: stop waiting out the minimum spin. During it:
      // land every cell still turning.
      skipMinSpinRef.current?.();
      holdAndWinBoardRef.current?.quickStop();
    }
  }, [dispatch, holdAndWinBoardRef, runRespin, stopPulse]);

  /**
   * ── The speed button switched to turbo mid-respin (2026-10-23) ── the base
   * game's rule: the round in the air lands NOW (a quick stop), the next one
   * takes the new speed. Guarded so a spammed button can do nothing more:
   * only while this respin's cells are turning (never in its collections),
   * and once per respin — the same quickStopRef a press of the play button
   * raises, so the two can never stop it twice. True if it stopped.
   */
  const requestTurboStop = useCallback(() => {
    if (!inFlightRef.current || !cellsTurningRef.current || quickStopRef.current) return false;
    quickStopRef.current = true;
    // Before the answer: stop waiting out the minimum spin. After it: land
    // every cell still turning.
    skipMinSpinRef.current?.();
    holdAndWinBoardRef.current?.quickStop();
    return true;
  }, [holdAndWinBoardRef]);

  // ── Spacebar ── only while the room owns the screen.
  useEffect(() => {
    if (!active) return undefined;
    const onKeyDown = (event) => {
      if (event.code !== "Space" && event.key !== " ") return;
      event.preventDefault();
      if (event.repeat) return; // a held key is not a second press
      handlePress();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active, handlePress]);

  // ── The end: End Collector, then the Total Win (GDD "End of Hold & Win") ──
  //
  // Once the round is SETTLED and nothing is turning — the last respin has
  // landed and its chests have collected — or on a reload straight into a
  // settled round, which would otherwise sit there with nothing to press.
  // Once per visit. The popup's close starts the wipe out (TransitionDialog).
  const collectorRef = useRef(null);
  const outroStartedRef = useRef(false);
  // ── The End Collector, built AHEAD (2026-10-07) ── its spine (the
  // session's first parse of hold_and_win_collector), atlas and meshes were
  // built at the outro, on screen. Now as soon as the room is up and the
  // counter is mounted; the outro below reuses it, the room's exit destroys it.
  useEffect(() => {
    if (!active) return undefined;
    return whenReady(
      () => {
        const counter = holdAndWinRespinCounterRef.current;
        return counter?.isReady?.() && counter.parentContainer ? counter : null;
      },
      (counter) => {
        if (collectorRef.current) return;
        collectorRef.current = new HoldAndWinCollectorController(counter.parentContainer);
        SpineAtlasUploader.upload("hold_and_win_collector");
        const spine = collectorRef.current.spine;
        if (spine) queueMeshWarm({ spine, isBusy: () => spine.destroyed || spine.visible });
      },
    );
  }, [active, holdAndWinRespinCounterRef]);
  useEffect(() => {
    if (!active || phase !== "settled" || spinning || inFlightRef.current) return undefined;
    if (outroStartedRef.current) return undefined;
    if (transitionActive || introPopupActive || errorPopupActive) return undefined;
    // ── Not under the free-rounds message (2026-10-19) ── a reload in a
    // campaign onto a settled round opens on "N FREE ROUNDS LEFT"; the
    // collection waits until it is closed, as the free spins' outro does.
    if (infoPopupActive) return undefined;
    if (freeSpinsReloadOwed) return undefined;
    // The room's parts may still be mounting (a reload): wait for them. A
    // re-run while waiting cancels this wait and starts its own — the outro
    // counts as started only once it actually runs.
    return whenReady(
      () => {
        const counter = holdAndWinRespinCounterRef.current;
        const overlay = holdAndWinBoardRef.current?.overlay?.();
        return counter?.isReady?.() && overlay ? { counter, overlay } : null;
      },
      async ({ counter, overlay }) => {
        if (outroStartedRef.current) return;
        outroStartedRef.current = true;
        // A reload into a settled round is parked on the button — not any more.
        if (store.getState().holdAndWin.reloadPhase !== "none") {
          dispatch(clearHoldAndWinReload());
        }
        stopPulse();
        // Presses now skip the collection (handlePress, second branch).
        inFlightRef.current = true;
        quickStopRef.current = false;
        setSettling(true);
        if (!collectorRef.current) {
          collectorRef.current = new HoldAndWinCollectorController(counter.parentContainer);
        }
        const st = store.getState().holdAndWin;
        // What was PAID; a restore may not carry the step's win.
        const paid = st.step?.win || st.accumulatedPayout || 0;
        // ── The max-win cap (2026-10-01) ── Pot Collection's two checks: the
        // server's flag (storage.win_limit_reached), or the paid amount at
        // MAX_WIN_MULTIPLIER x bet. Capped, the collection stops at the cap
        // and Max Win replaces Total Win.
        const game = store.getState().game;
        const bet = game?.currentRound?.bet || game?.bet || 0;
        const maxWin = (FEATURES_CONFIG.HOLD_AND_WIN?.MAX_WIN_MULTIPLIER || 10000) * bet;
        const capped = !!st.winLimitReached || (bet > 0 && paid >= maxWin);
        // The End Collector at the player's speed now (2026-10-23): turbo one
        // by one but fast, super turbo as a skip.
        paceFromPlayer();
        await playEndCollector({
          values: st.values,
          unlockedRows: st.unlockedRows,
          // win: st.step?.win || st.accumulatedPayout || 0,
          win: paid,
          capped,
          // The same breath the loop takes before another respin.
          settleDelayMs: spinConfig().RESPIN_NEXT_DELAY_MS ?? 700,
          overlay,
          trailLayer: holdAndWinGameViewControllerRef.current?.trailLayer,
          counter,
          collector: collectorRef.current,
          // isSkipped: () => quickStopRef.current,
          // Super turbo: always as a skip — resetSkip cannot take that back.
          isSkipped: () => quickStopRef.current || collectAlwaysSkipped(),
          // The Scarab reveal, before the collector (GDD step 2).
          scarabs: st.step?.scarabs ?? null,
          scarabMultipliers: st.scarabMultipliers ?? null,
          // The box zooms out, then the collector comes (2026-10-14).
          zoomOut: () => holdAndWinGameViewControllerRef.current?.playReelZoomOut?.(),
          // ── The Royal (GDD End Collector step 6, contract §8) ── after the
          // coins it joins the collector by its own award animation.
          royal: st.royalJackpot ? selectHoldAndWinRoyalAmount(store.getState()) : 0,
          // playRoyal: () =>
          //   new Promise((resolve) => {
          //     dispatch(setShowHoldAndWinRoyal(true));
          //     const unsub = store.subscribe(() => {
          //       if (!store.getState().holdAndWin.showRoyal) {
          //         unsub();
          //         resolve();
          //       }
          //     });
          //   }),
          // The popup played on the respin that filled the grid; here the
          // Royal flies off its plate into the collector (GDD step 6).
          royalSource: holdAndWinGameViewControllerRef.current?.jackpotPlateLabel?.("royal") ?? null,
          resetSkip: () => {
            quickStopRef.current = false;
          },
        });
        inFlightRef.current = false;
        quickStopRef.current = false;
        setSettling(false);
        // dispatch(setShowHoldAndWinTotalWin(true));
        // dispatch(capped ? setShowHoldAndWinMaxWin(true) : setShowHoldAndWinTotalWin(true));
        // ── Inside free spins, the round's Max Win is THEIRS (2026-10-06) ──
        // The bonus shows its own Total Win; back in free spins the room opens
        // the one Max Win for the whole round — never two (the free-rounds
        // rule: the round's ending waits for the bonus and shows once).
        const roundEndsHere = !store.getState().freeSpins.active;
        dispatch(
          capped && roundEndsHere
            ? setShowHoldAndWinMaxWin(true)
            : setShowHoldAndWinTotalWin(true),
        );
      },
    );
  }, [
    active,
    phase,
    spinning,
    transitionActive,
    introPopupActive,
    errorPopupActive,
    infoPopupActive,
    freeSpinsReloadOwed,
    dispatch,
    holdAndWinBoardRef,
    holdAndWinRespinCounterRef,
    holdAndWinGameViewControllerRef,
    stopPulse,
  ]);

  // ── Leaving the room ──
  useEffect(() => {
    if (active) return;
    // The collector goes with the room; the next visit starts on the counter.
    collectorRef.current?.destroy();
    collectorRef.current = null;
    outroStartedRef.current = false;
    clearTimers();
    loopTimerRef.current?.clear();
    loopTimerRef.current = null;
    inFlightRef.current = false;
    quickStopRef.current = false;
    failedRef.current = false;
    firstRespinRef.current = true;
    setSpinning(false);
    setSettling(false);
    stopPulse();
    // Empty again for the next visit.
    counterShownRef.current = false;
    holdAndWinRespinCounterRef.current?.setValue(0);
    // The jackpot plates the round lit go dark again (they stay lit for the
    // whole feature — the coins stay on the board).
    holdAndWinGameViewControllerRef.current?.releaseJackpotHits?.();
    if (playButtonRef.current) {
      anime.remove(playButtonRef.current);
      anime.remove(playButtonRef.current.scale);
      playButtonRef.current.rotation = 0;
    }
  }, [
    active,
    clearTimers,
    holdAndWinRespinCounterRef,
    holdAndWinGameViewControllerRef,
    playButtonRef,
    stopPulse,
  ]);

  useEffect(() => clearTimers, [clearTimers]);

  const value = useMemo(
    () => ({
      handlePress,
      requestTurboStop,
      spinning,
      settling,
      awaitingResume,
      displayRespins,
    }),
    // [handlePress, spinning, settling, awaitingResume, displayRespins],
    [handlePress, requestTurboStop, spinning, settling, awaitingResume, displayRespins],
  );

  return (
    <HoldAndWinControlContext.Provider value={value}>
      {children}
    </HoldAndWinControlContext.Provider>
  );
};

export const useHoldAndWinControl = () => useContext(HoldAndWinControlContext);
